import React from 'react';
import './About.css';

const aboutImages = [
  { id: 1, src: '/about-1.png' },
  { id: 2, src: '/about-2.png' },
  { id: 3, src: '/about-3.png' },
  { id: 4, src: '/about-4-1.png' },
  { id: 5, src: '/about-5-1.png' },
  { id: 6, src: '/about-6.png' },
  { id: 7, src: '/about-7.png' },
  { id: 8, src: '/about-8.png' },
  { id: 9, src: '/about-9.png' },
  { id: 10, src: '/about-10.png' },
  { id: 11, src: '/about-11.png' },
  { id: 12, src: '/about-12.png' },
  { id: 13, src: '/about-13.png' },
  { id: 14, src: '/about-14.png' },
];

export default function About({ navigate }) {
  return (
    <div className="about-page">
      <div className="page-banner">
        <div className="container">
          <h1>About Us</h1>
          <p>Redefining Nightlife Experiences in Australia</p>
        </div>
      </div>

      <section className="about-intro">
        <div className="container">
          <div className="intro-card">
            <h2>What is <span className="gradient-text">HiveeClub?</span></h2>
            <p>
              HiveeClub is a social and nightlife platform designed to help people discover events, connect with others,
              and enjoy seamless ticketing experiences. We combine nightlife discovery, social connection, and event
              booking into one seamless experience.
            </p>
            <p>
              Whether you're booking a VIP table at WAO Superclub, discovering top DJs, or connecting with people
              you met at an event, HiveeClub is designed to make every night out more social and memorable.
            </p>
          </div>
        </div>
      </section>

      <section className="values-section">
        <div className="container">
          <div className="section-header-small">
            <span className="section-tag">Our Values</span>
            <h2>Built for the <span className="gradient-text">Night Owls</span></h2>
          </div>
          <div className="values-grid">
            {[
              { icon: '🎯', title: 'Seamless Experience', desc: 'From discovery to entry, every step is designed to be frictionless and enjoyable.' },
              { icon: '🤝', title: 'Community First', desc: 'We believe the best nights are shared. Our platform connects people who love the same experiences.' },
              { icon: '🔒', title: 'Trusted & Secure', desc: 'Your bookings, payments, and personal data are protected with enterprise-grade security.' },
              { icon: '🌟', title: 'Premium Quality', desc: 'We partner only with the hottest venues and events to ensure an unforgettable experience.' },
            ].map(v => (
              <div key={v.title} className="value-card">
                <div className="value-icon">{v.icon}</div>
                <h3>{v.title}</h3>
                <p>{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="features-img-section">
        <div className="container">
          <div className="section-header-small">
            <span className="section-tag">How It Works</span>
            <h2>Everything You Need to <span className="gradient-text">Get Started</span></h2>
            <p>From registration to booking — here's what HiveeClub can do for you.</p>
          </div>
          <div className="features-img-grid">
            {aboutImages.map((img) => (
              <div key={img.id} className="features-img-card">
                <img src={img.src} alt={`HiveeClub feature ${img.id}`} loading="lazy" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* App Showcase */}
      <section className="app-showcase-section">
        <div className="container">
          <div className="section-header-small">
            <span className="section-tag">The App</span>
            <h2>Experience <span className="gradient-text">HiveeClub</span></h2>
            <p>Available free on iOS and Android. Download now and start your nightlife journey.</p>
          </div>
          <div className="app-showcase-grid">
            <div className="app-showcase-visual">
              <div className="app-phone-frame">
                <img src="/app-screenshot.png" alt="HiveeClub App Home" className="app-screenshot" />
              </div>
              <div className="app-glow-bg" />
            </div>
            <div className="app-showcase-content">
              <div className="app-feature-list">
                <div className="app-feature-item">
                  <div className="app-feature-icon">
                    <img src="/social-img.png" alt="Social" onError={(e)=>{e.target.style.display='none'}} />
                    <span>💬</span>
                  </div>
                  <div>
                    <h4>Social Platform</h4>
                    <p>Connect with friends, explore profiles, and share moments from your nights out — all within the HiveeClub community.</p>
                  </div>
                </div>
                <div className="app-feature-item">
                  <div className="app-feature-icon">
                    <img src="/ticket-img.png" alt="Tickets" onError={(e)=>{e.target.style.display='none'}} />
                    <span>🎟</span>
                  </div>
                  <div>
                    <h4>Instant Ticketing</h4>
                    <p>Book tickets, reserve VIP tables, and plan birthday celebrations — all in one place. Fast, easy, and paperless.</p>
                  </div>
                </div>
                <div className="app-feature-item">
                  <div className="app-feature-icon"><span>🎧</span></div>
                  <div>
                    <h4>DJ Platform</h4>
                    <p>Discover resident DJs, explore emerging artists, and stay updated on upcoming events.</p>
                  </div>
                </div>
                <div className="app-feature-item">
                  <div className="app-feature-icon"><span>🏆</span></div>
                  <div>
                    <h4>Rewards Program</h4>
                    <p>Earn points with every booking and unlock exclusive Gold, Platinum, and Diamond member benefits.</p>
                  </div>
                </div>
              </div>
              <div className="app-store-row">
                <a href="https://apps.apple.com/au/app/hiveeclub/id1584271794" target="_blank" rel="noopener noreferrer" className="store-btn apple">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                  <div><small>Download on the</small><strong>App Store</strong></div>
                </a>
                <a href="https://play.google.com/store/apps/details?id=com.hivee.hivee" target="_blank" rel="noopener noreferrer" className="store-btn google">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M3.18 23.76c.33.18.7.24 1.08.17l12.29-7.08-2.6-2.6-10.77 9.51zm-1.06-1.38V1.62L13.64 12 2.12 22.38zm17.3-11.43L16.8 9.4 14 12l2.8 2.8 2.66-1.54a1.5 1.5 0 0 0 0-2.52zM4.26.07L16.55 7.15 13.95 9.75 3.18.24A1.5 1.5 0 0 0 4.26.07z"/></svg>
                  <div><small>Get it on</small><strong>Google Play</strong></div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="about-cta">
        <div className="container">
          <div className="cta-card">
            <h2>Ready to Join the Hive?</h2>
            <p>Discover a smarter way to experience nightlife.</p>
            <div className="cta-buttons-row">
              <a href="https://apps.apple.com/au/app/hiveeclub/id1584271794" target="_blank" rel="noopener noreferrer" className="btn-primary">
                Download on the App Store
              </a>
              <button className="btn-outline" onClick={() => navigate('contact')}>
                Contact Us
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
